import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Text } from '@teals/ui-web';
import { useAppDispatch } from '../../store';
import { setCredentials } from '../../store/slices';
import {
  AccountLinkRequiredError,
  completeGoogleRedirect,
  getSsoFlow,
} from '../../services/cognito';
import { normalizeAuthEmail } from '../../utils/ssoLinkEmail';

const Container = styled.div`
  width: 100%;
  max-width: 380px;
  text-align: center;
`;

const Lead = styled.p`
  margin: 0.75rem 0 0;
  color: var(--text-secondary);
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;

    completeGoogleRedirect(location.search)
      .then((result) => {
        if (cancelled) return;
        dispatch(
          setCredentials({
            user: {
              id: result.user.userId,
              email: result.user.email,
              name: result.user.name,
              organizationId: result.user.organizationId,
              groups: result.user.groups,
            },
            token: result.tokens.idToken,
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
          })
        );
        if (result.user.organizationId && getSsoFlow() === 'google') {
          navigate(
            `/agents?onboarding=member&joined=${encodeURIComponent(result.user.organizationId)}`,
            { replace: true }
          );
          return;
        }
        navigate('/agents', { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof AccountLinkRequiredError) {
          const params = new URLSearchParams();
          const email = normalizeAuthEmail(err.email);
          if (email) params.set('email', email);
          navigate(`/auth/link?${params.toString()}`, { replace: true });
          return;
        }
        const text =
          err instanceof Error ? err.message : 'Google sign-in did not complete.';
        navigate(`/auth?error=${encodeURIComponent(text)}`, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.search, navigate]);

  return (
    <Container>
      <Text variant="h3">Completing Google sign-in…</Text>
      <Lead>
        Google confirmed your account. Next we’ll link, create an organisation, or
        take you into Luminafactory.
      </Lead>
    </Container>
  );
};

export default AuthCallback;
